import type { ResponseData } from '@vrplatform/data';
import { day, utc } from '@vrplatform/utils';

export type ListingJournalMetricRow =
  ResponseData<'get:/metrics/listing-revenue'>['data'][number];

export type ListingBalancesChartPoint = {
  date: string;
  ownerLockedRevenue: number;
  ownerUnlockedRevenue: number;
  ownerRevenue: number;
  managerLockedRevenue: number;
  managerUnlockedRevenue: number;
  managerRevenue: number;
  currency?: string;
  isFuture: boolean;
};

export type ListingBalancesChartTotals = {
  owners: number;
  manager: number;
};

export type ListingBalancesChartData = {
  chartData: ListingBalancesChartPoint[];
  currency?: string;
  totals: ListingBalancesChartTotals;
};

type GetListingBalancesChartRangeProps = {
  statementStartAt?: string | null;
  today?: string;
};

type BuildListingBalancesChartDataProps = {
  currentMonth: string;
  managerRows: ListingJournalMetricRow[];
  months: string[];
  ownerRows: ListingJournalMetricRow[];
};

const toMonthKey = (date: string) => utc(date).startOf('month').yyyymmdd();

type RevenueSegments = {
  lockedRevenue: number;
  unlockedRevenue: number;
};

const getMonthlyRevenueSegments = (rows: ListingJournalMetricRow[]) => {
  const monthlyTotals = new Map<string, RevenueSegments>();

  for (const row of rows) {
    const monthKey = toMonthKey(row.date);
    const previous = monthlyTotals.get(monthKey) ?? {
      lockedRevenue: 0,
      unlockedRevenue: 0,
    };

    monthlyTotals.set(monthKey, {
      lockedRevenue: previous.lockedRevenue + (row.lockedRevenue ?? 0),
      unlockedRevenue: previous.unlockedRevenue + (row.unlockedRevenue ?? 0),
    });
  }

  return monthlyTotals;
};

const getCurrency = (...rows: ListingJournalMetricRow[][]) =>
  rows.flat().find((row) => row.currency)?.currency;

export const getListingBalancesChartRange = ({
  statementStartAt,
  today = day().yyyymmdd(),
}: GetListingBalancesChartRangeProps) => {
  const currentMonth = day(today).startOf('month').yyyymmdd();
  const threeMonthsAgo = day(today)
    .subtract(3, 'month')
    .startOf('month')
    .yyyymmdd();
  const chartEndDate = day(today).add(2, 'month').endOf('month').yyyymmdd();
  const statementStartMonth =
    statementStartAt && day(statementStartAt).isValid()
      ? day(statementStartAt).startOf('month').yyyymmdd()
      : threeMonthsAgo;

  const startDate = day(statementStartMonth).isBefore(threeMonthsAgo)
    ? threeMonthsAgo
    : statementStartMonth;

  const months = Array.from(
    { length: day(chartEndDate).diff(startDate, 'month') + 1 },
    (_, index) => day(startDate).add(index, 'month').yyyymmdd()
  );

  return {
    apiEndDate: day(chartEndDate).add(1, 'day').yyyymmdd(),
    chartEndDate,
    currentMonth,
    months,
    startDate,
  };
};

export const getListingBalancesChartData = ({
  currentMonth,
  managerRows,
  months,
  ownerRows,
}: BuildListingBalancesChartDataProps): ListingBalancesChartData => {
  const ownerTotalsByMonth = getMonthlyRevenueSegments(ownerRows);
  const managerTotalsByMonth = getMonthlyRevenueSegments(managerRows);
  const currency = getCurrency(ownerRows, managerRows);

  const chartData: ListingBalancesChartPoint[] = months
    .map((date) => {
      const ownerRevenue = ownerTotalsByMonth.get(date) ?? {
        lockedRevenue: 0,
        unlockedRevenue: 0,
      };
      const managerRevenue = managerTotalsByMonth.get(date) ?? {
        lockedRevenue: 0,
        unlockedRevenue: 0,
      };

      return {
        date,
        ownerLockedRevenue: ownerRevenue.lockedRevenue,
        ownerUnlockedRevenue: ownerRevenue.unlockedRevenue,
        ownerRevenue: ownerRevenue.lockedRevenue + ownerRevenue.unlockedRevenue,
        managerLockedRevenue: managerRevenue.lockedRevenue,
        managerUnlockedRevenue: managerRevenue.unlockedRevenue,
        managerRevenue:
          managerRevenue.lockedRevenue + managerRevenue.unlockedRevenue,
        currency,
        isFuture: date > currentMonth,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    chartData,
    currency,
    totals: {
      manager: chartData.reduce(
        (total, point) => total + point.managerRevenue,
        0
      ),
      owners: chartData.reduce((total, point) => total + point.ownerRevenue, 0),
    },
  };
};
